/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { Spin, TreeSelect } from 'antd';
import { TreeNodeNormal } from 'antd/lib/tree-select/interface';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppState } from '../../../../redux/redux.model';
import { SeasonTree, UUID } from '../../../../redux/models';
import { getSeasonsTreeByCompany } from '../../../../redux/company/service';
import {
  collectVisibleKeys,
  dropOrphanedSelections,
  encodeSavedSeasonIds,
  expandOrgKeysToSeasons,
  filterSeasonsTreeByCrop,
  nodeKey,
  OrgLoadState,
  parseOrgKey,
  stripOrgKeys,
  toOrgRoot,
  toSeasonNode
} from './workspaceSeasonsTreeSelect.utils';
import './workspaceSeasonsTreeSelect.style.scss';

export interface WorkspaceSeasonsTreeSelectProps {
  name: string;
  workspaceId?: string;
  selectedOrgId?: string;
  cropId?: string;
  disabled?: boolean;
  savedSeasonIds?: string[];
  control: Control<FieldValues>;
  onDropdownVisibleChange?: (open: boolean) => void;
  onSeasonsTreeChange?: (trees: SeasonTree[]) => void;
}

type LoadDataNode = { props: { eventKey: string } };

interface WorkspaceSeasonsState {
  orgsTree: TreeNodeNormal[];
  orgLoadState: Record<UUID, OrgLoadState>;
  seasonsByOrg: Record<UUID, SeasonTree[]>;
}

const initialState: WorkspaceSeasonsState = {
  orgsTree: [],
  orgLoadState: {},
  seasonsByOrg: {}
};

const buildOrgChildren = (orgId: UUID, orgName: string, seasonsByOrg: Record<UUID, SeasonTree[]>, cropId?: string): TreeNodeNormal[] => {
  const seasons = seasonsByOrg[orgId];
  if (!seasons) return [];
  return filterSeasonsTreeByCrop(seasons, cropId).map(toSeasonNode(orgId, orgName));
};

const spinnerPlaceholder = (orgId: UUID): TreeNodeNormal[] => [
  {
    value: nodeKey('org', [orgId, '__loading__']),
    key: nodeKey('org', [orgId, '__loading__']),
    title: (<Spin size='small' className='workspace-seasons-tree-select__spinner' />) as any,
    selectable: false,
    disableCheckbox: true,
    isLeaf: true
  }
];

const errorPlaceholder = (orgId: UUID, onRetry: () => void): TreeNodeNormal[] => [
  {
    value: nodeKey('org', [orgId, '__error__']),
    key: nodeKey('org', [orgId, '__error__']),
    title: (
      <button type='button' className='workspace-seasons-tree-select__org-error' onClick={onRetry} aria-label={`retry-org-${orgId}`}>
        Failed to load. Retry
      </button>
    ) as any,
    selectable: false,
    disableCheckbox: true,
    isLeaf: true
  }
];

const orgsTreeMatchesWorkspace = (orgsTree: TreeNodeNormal[], orgs: { id: UUID }[]): boolean =>
  orgsTree.length === orgs.length &&
  orgsTree.every(node => {
    const orgId = parseOrgKey(node.key);
    return orgId !== null && orgs.some(org => org.id === orgId);
  });

const findOrgNode = (orgsTree: TreeNodeNormal[], orgId: UUID): TreeNodeNormal | undefined =>
  orgsTree.find(n => n.key === nodeKey('org', [orgId]));

// Mutate-then-shallow-clone: rc-tree-select 2.9 keeps an internal `dataRef`
// keyed to TreeNodeNormal identity (see rc-tree-select/es/util.js). A fully
// immutable rebuild gives every node a new identity, which drops the
// expansion / loading state for that node and causes the spinner to flicker.
// We assign the new children on the existing node, then shallow-clone the
// outer array so React still re-renders.
const setOrgLoading = (state: WorkspaceSeasonsState, orgId: UUID): WorkspaceSeasonsState => {
  const targetOrg = findOrgNode(state.orgsTree, orgId);
  if (targetOrg) targetOrg.children = spinnerPlaceholder(orgId);
  return {
    ...state,
    orgLoadState: { ...state.orgLoadState, [orgId]: 'loading' },
    orgsTree: [...state.orgsTree]
  };
};

// See setOrgLoading: same rc-tree-select dataRef identity constraint.
const setOrgError = (state: WorkspaceSeasonsState, orgId: UUID, onRetry: () => void): WorkspaceSeasonsState => {
  const targetOrg = findOrgNode(state.orgsTree, orgId);
  if (targetOrg) targetOrg.children = errorPlaceholder(orgId, onRetry);
  return {
    ...state,
    orgLoadState: { ...state.orgLoadState, [orgId]: 'error' },
    orgsTree: [...state.orgsTree]
  };
};

// See setOrgLoading: same rc-tree-select dataRef identity constraint.
// Re-enable the org checkbox once seasons resolve so rc-tree-select can propagate half-checked state from descendants.
// Until then, toOrgRoot keeps disableCheckbox=true to avoid silent empty-selection when a user clicks an org before children load.
const setOrgLoaded = (
  state: WorkspaceSeasonsState,
  orgId: UUID,
  orgName: string,
  seasons: SeasonTree[],
  cropId: string | undefined
): WorkspaceSeasonsState => {
  const nextSeasonsByOrg = { ...state.seasonsByOrg, [orgId]: seasons };
  const targetOrg = findOrgNode(state.orgsTree, orgId);
  if (targetOrg) {
    targetOrg.children = buildOrgChildren(orgId, orgName, nextSeasonsByOrg, cropId);
    targetOrg.disableCheckbox = false;
  }
  return {
    ...state,
    orgLoadState: { ...state.orgLoadState, [orgId]: 'loaded' },
    seasonsByOrg: nextSeasonsByOrg,
    orgsTree: [...state.orgsTree]
  };
};

const applyForkJoinResult = (
  state: WorkspaceSeasonsState,
  result: { orgId: UUID; seasons: SeasonTree[] | null },
  getOrgName: (orgId: UUID) => string,
  cropId: string | undefined,
  retryFor: (orgId: UUID) => () => void
): WorkspaceSeasonsState => {
  if (result.seasons === null) return setOrgError(state, result.orgId, retryFor(result.orgId));
  return setOrgLoaded(state, result.orgId, getOrgName(result.orgId), result.seasons, cropId);
};

// Module-level reducer keeps the subscribe callback shallow (Sonar S2004).
const reduceForkJoinResults = (
  state: WorkspaceSeasonsState,
  results: Array<{ orgId: UUID; seasons: SeasonTree[] | null }>,
  getOrgName: (orgId: UUID) => string,
  cropId: string | undefined,
  retryFor: (orgId: UUID) => () => void
): WorkspaceSeasonsState => results.reduce((acc, result) => applyForkJoinResult(acc, result, getOrgName, cropId, retryFor), state);

// See setOrgLoading: same rc-tree-select dataRef identity constraint.
const refreshChildrenForCrop = (
  state: WorkspaceSeasonsState,
  getOrgName: (orgId: UUID) => string,
  cropId: string | undefined
): WorkspaceSeasonsState => {
  if (!Object.keys(state.seasonsByOrg).length) return state;
  Object.keys(state.seasonsByOrg).forEach(orgId => {
    const targetOrg = findOrgNode(state.orgsTree, orgId);
    if (targetOrg) targetOrg.children = buildOrgChildren(orgId, getOrgName(orgId), state.seasonsByOrg, cropId);
  });
  return { ...state, orgsTree: [...state.orgsTree] };
};

const WorkspaceSeasonsTreeSelect = ({
  name,
  workspaceId,
  selectedOrgId,
  cropId,
  disabled,
  savedSeasonIds,
  control,
  onDropdownVisibleChange,
  onSeasonsTreeChange
}: WorkspaceSeasonsTreeSelectProps) => {
  const workspaceOrgs = useSelector((state: AppState) => state.Workspace.orgs, shallowEqual);
  const orgsLoadedFor = useSelector((state: AppState) => state.Workspace.orgsWorkspaceId);
  const orgsMatchWorkspace = orgsLoadedFor === workspaceId;

  const orgNameById = useMemo(
    () => workspaceOrgs.reduce<Record<UUID, string>>((acc, org) => ({ ...acc, [org.id]: org.name }), {}),
    [workspaceOrgs]
  );
  const getOrgName = useCallback((orgId: UUID) => orgNameById[orgId] ?? '', [orgNameById]);

  const [state, setState] = useState<WorkspaceSeasonsState>(initialState);
  const subscriptions = useRef<Subscription[]>([]);
  const editPrefetchedRef = useRef<string | undefined>(undefined);
  const previousWorkspaceRef = useRef<string | undefined>(workspaceId);
  const loadStateRef = useRef<Record<UUID, OrgLoadState>>({});

  useEffect(() => {
    return () => {
      subscriptions.current.forEach(sub => sub.unsubscribe());
      subscriptions.current = [];
    };
  }, []);

  useEffect(() => {
    if (previousWorkspaceRef.current !== workspaceId) {
      subscriptions.current.forEach(sub => sub.unsubscribe());
      subscriptions.current = [];
      setState(initialState);
      loadStateRef.current = {};
      editPrefetchedRef.current = undefined;
      previousWorkspaceRef.current = workspaceId;
    }
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId || !orgsMatchWorkspace) return;
    setState(prev => {
      if (prev.orgsTree.length && orgsTreeMatchesWorkspace(prev.orgsTree, workspaceOrgs)) return prev;
      const existingNodes = new Map(prev.orgsTree.map(node => [parseOrgKey(node.key), node]));
      return {
        ...prev,
        orgsTree: workspaceOrgs.map(org => existingNodes.get(org.id) ?? toOrgRoot(org))
      };
    });
  }, [workspaceId, workspaceOrgs, orgsMatchWorkspace]);

  const trackSubscription = useCallback((sub: Subscription): void => {
    // Keep the cleanup list bounded: remove each subscription from the array
    // as soon as the underlying observable completes, so unmount does not
    // iterate stale entries.
    sub.add(() => {
      subscriptions.current = subscriptions.current.filter(entry => entry !== sub);
    });
    subscriptions.current.push(sub);
  }, []);

  const loadOrgSeasons = useCallback(
    (orgId: UUID, onSettle?: () => void): void => {
      const current = loadStateRef.current[orgId];
      if (current === 'loading' || current === 'loaded' || current === 'error') {
        onSettle?.();
        return;
      }
      loadStateRef.current[orgId] = 'loading';
      setState(prev => setOrgLoading(prev, orgId));
      const handleRetry = () => {
        loadStateRef.current[orgId] = 'idle';
        loadOrgSeasons(orgId);
      };
      const sub = getSeasonsTreeByCompany(orgId)
        .pipe(
          map(response => response.data),
          catchError(() => of(null))
        )
        .subscribe(result => {
          if (result === null) {
            loadStateRef.current[orgId] = 'error';
            setState(prev => setOrgError(prev, orgId, handleRetry));
          } else {
            loadStateRef.current[orgId] = 'loaded';
            setState(prev => setOrgLoaded(prev, orgId, getOrgName(orgId), result, cropId));
          }
          onSettle?.();
        });
      trackSubscription(sub);
    },
    [cropId, getOrgName, trackSubscription]
  );

  const handleLoadData = useCallback(
    (node: LoadDataNode): Promise<void> => {
      return new Promise<void>(resolve => {
        const orgId = node?.props?.eventKey ? parseOrgKey(node.props.eventKey) : null;
        if (!orgId) {
          resolve();
          return;
        }
        loadOrgSeasons(orgId, resolve);
      });
    },
    [loadOrgSeasons]
  );

  const handleTreeExpand = useCallback(
    (expandedKeys: string[]) => {
      expandedKeys.forEach(key => {
        const orgId = parseOrgKey(key);
        if (orgId) loadOrgSeasons(orgId);
      });
    },
    [loadOrgSeasons]
  );

  // Edit pre-expansion fallback: forkJoin across loaded workspace orgs when saved seasons are present
  // and no per-season org_id is provided by BE. Tracked under Section 14 question 2.
  useEffect(() => {
    if (!workspaceId || !orgsMatchWorkspace) return;
    if (!savedSeasonIds?.length) return;
    if (!workspaceOrgs.length) return;
    if (editPrefetchedRef.current === workspaceId) return;
    editPrefetchedRef.current = workspaceId;
    const orgIds = workspaceOrgs.map(org => org.id);
    orgIds.forEach(id => {
      loadStateRef.current[id] = 'loading';
    });
    setState(prev => ({
      ...prev,
      orgLoadState: orgIds.reduce<Record<UUID, OrgLoadState>>((acc, id) => ({ ...acc, [id]: 'loading' }), prev.orgLoadState)
    }));
    const retryFor = (orgId: UUID) => () => {
      loadStateRef.current[orgId] = 'idle';
      loadOrgSeasons(orgId);
    };
    const sub = forkJoin(
      orgIds.map(orgId =>
        getSeasonsTreeByCompany(orgId).pipe(
          map(response => ({ orgId, seasons: response.data })),
          catchError(() => of({ orgId, seasons: null as SeasonTree[] | null }))
        )
      )
    ).subscribe(results => {
      results.forEach(result => {
        loadStateRef.current[result.orgId] = result.seasons === null ? 'error' : 'loaded';
      });
      setState(prev => reduceForkJoinResults(prev, results, getOrgName, cropId, retryFor));
    });
    trackSubscription(sub);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, orgsMatchWorkspace, savedSeasonIds, workspaceOrgs]);

  // seasonsByOrg is a load-bearing dep: it re-filters seasons that resolved against a stale
  // (un-hydrated) crop, which no later crop change would otherwise correct (P40-60665).
  useEffect(() => {
    setState(prev => refreshChildrenForCrop(prev, getOrgName, cropId));
  }, [cropId, getOrgName, state.seasonsByOrg]);

  const visibleOrgTree = useMemo(
    () => (selectedOrgId ? state.orgsTree.filter(node => parseOrgKey(node.key) === selectedOrgId) : state.orgsTree),
    [state.orgsTree, selectedOrgId]
  );
  const visibleKeys = useMemo(() => collectVisibleKeys(visibleOrgTree), [visibleOrgTree]);

  // Flatten per-org seasons so the parent form can resolve selected season/property/area ids
  // against the workspace tree on submit (parseSeasonIdsToSeasonsDeep needs this lookup).
  useEffect(() => {
    if (!onSeasonsTreeChange) return;
    const aggregated = Object.values(state.seasonsByOrg).flat();
    onSeasonsTreeChange(aggregated);
  }, [state.seasonsByOrg, onSeasonsTreeChange]);

  // Pre-encode saved season ids into encoded keys once seasonsByOrg is populated
  const initialEncodedValue = useMemo(() => {
    if (!savedSeasonIds?.length) return undefined;
    return encodeSavedSeasonIds(savedSeasonIds, state.seasonsByOrg);
  }, [savedSeasonIds, state.seasonsByOrg]);

  // RHF v5 Controller binds defaultValue only on mount. Remount once initialEncodedValue
  // is seeded (savedSeasonIds + seasonsByOrg both ready) so the field actually carries
  // the encoded keys into the form state on edit.
  const controllerKey = (initialEncodedValue ?? []).join(',');

  return (
    <Controller
      key={controllerKey}
      name={name}
      control={control}
      defaultValue={initialEncodedValue}
      onChange={([value]) => stripOrgKeys(dropOrphanedSelections(expandOrgKeysToSeasons(value, visibleOrgTree), visibleKeys))}
      as={
        <TreeSelect
          id={name}
          className='st-tree workspace-seasons-tree-select'
          treeData={visibleOrgTree}
          loadData={handleLoadData}
          onTreeExpand={handleTreeExpand}
          treeCheckable
          showCheckedStrategy='SHOW_PARENT'
          treeNodeFilterProp='title'
          treeNodeLabelProp='label'
          disabled={disabled}
          onDropdownVisibleChange={onDropdownVisibleChange}
          allowClear
        />
      }
    />
  );
};

export default WorkspaceSeasonsTreeSelect;
